import { useNavigate } from 'react-router-dom';
import type { Invoice, Ticket } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { DataState } from '../../components/ui/DataState.js';
import { formatDate } from '../../utils/date.js';
import { useInvoices } from '../invoices/api.js';
import { INVOICE_STATUS_LABELS, INVOICE_STATUS_TONES } from '../invoices/status.js';
import { useTicketStatusOptions, useTickets } from '../tickets/api.js';
import { ticketDisplayId } from '../tickets/reference.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const HEAD_CLASS = 'px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500';
const CELL_CLASS = 'px-3 py-2 text-sm text-slate-700';

function Table({
  head,
  children,
  colSpan,
  ...state
}: {
  head: string[];
  children: React.ReactNode;
  colSpan: number;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  isEmpty: boolean;
  emptyTitle: string;
  onRetry?: () => void;
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full border-collapse">
        <thead className="border-b border-slate-200 bg-slate-50">
          <tr>
            {head.map((h) => (
              <th key={h} className={HEAD_CLASS}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          <DataState colSpan={colSpan} {...state}>
            {children}
          </DataState>
        </tbody>
      </table>
    </div>
  );
}

export function AgreementTicketsTab({ agreementId }: { agreementId: string }): JSX.Element {
  const navigate = useNavigate();
  const query = useTickets({ page: 1, pageSize: 200, agreementId });
  const statusesQuery = useTicketStatusOptions();
  const statusName = (id: string): string =>
    (statusesQuery.data ?? []).find((s) => s.id === id)?.name ?? '—';
  const rows = query.data?.data ?? [];
  return (
    <Table
      head={['#', 'Title', 'Status', 'Priority', 'Hours']}
      colSpan={5}
      isLoading={query.isLoading}
      isError={query.isError}
      error={query.error}
      isEmpty={rows.length === 0}
      emptyTitle="No tickets for this agreement."
      onRetry={() => void query.refetch()}
    >
      {rows.map((t: Ticket) => (
        <tr
          key={t.id}
          onClick={() => navigate(`/tickets/${t.id}`)}
          className="cursor-pointer hover:bg-slate-50"
        >
          <td className={`${CELL_CLASS} tabular-nums text-slate-500`}>{ticketDisplayId(t)}</td>
          <td className={`${CELL_CLASS} font-medium text-slate-900`}>{t.title}</td>
          <td className={CELL_CLASS}>{statusName(t.statusId)}</td>
          <td className={`${CELL_CLASS} capitalize`}>{t.priority}</td>
          <td className={`${CELL_CLASS} tabular-nums`}>{t.actualHours}</td>
        </tr>
      ))}
    </Table>
  );
}

export function AgreementInvoicesTab({ agreementId }: { agreementId: string }): JSX.Element {
  const navigate = useNavigate();
  const query = useInvoices({ page: 1, pageSize: 200, agreementId });
  const rows = query.data?.data ?? [];
  return (
    <Table
      head={['Number', 'Status', 'Issued', 'Due', 'Total']}
      colSpan={5}
      isLoading={query.isLoading}
      isError={query.isError}
      error={query.error}
      isEmpty={rows.length === 0}
      emptyTitle="No invoices for this agreement."
      onRetry={() => void query.refetch()}
    >
      {rows.map((inv: Invoice) => (
        <tr
          key={inv.id}
          onClick={() => navigate(`/invoices/${inv.id}`)}
          className="cursor-pointer hover:bg-slate-50"
        >
          <td className={`${CELL_CLASS} font-medium text-slate-900`}>{inv.number}</td>
          <td className={CELL_CLASS}>
            <Badge tone={INVOICE_STATUS_TONES[inv.status]}>
              {INVOICE_STATUS_LABELS[inv.status]}
            </Badge>
          </td>
          <td className={CELL_CLASS}>{formatDate(inv.issueDate)}</td>
          <td className={CELL_CLASS}>{formatDate(inv.dueDate)}</td>
          <td className={`${CELL_CLASS} tabular-nums`}>{currency.format(Number(inv.total))}</td>
        </tr>
      ))}
    </Table>
  );
}
